// Default `react-toastify` injects its CSS into <head> at runtime and beats
// unlayered `_toasts.scss`. This entry skips that injection; layout CSS is
// loaded into `@layer libraries` from main.scss instead.
import { useEffect, useState } from "react";
import {
    cssTransition,
    ToastContainer as ToastsContainerLib,
} from "react-toastify/unstyled";
import { TOAST_AUTO_CLOSE_MS } from "@lib/toastConstants";
import { AlertOutlinedIcon } from "@icons";

const customSlide = cssTransition({
    enter: "custom-slide-in",
    exit: "custom-slide-out",
    collapseDuration: 300,
    collapse: false,
});

const ConfiguredToastContainer: React.FC = () => (
    <ToastsContainerLib
        aria-label="toasts"
        position="bottom-left"
        hideProgressBar
        limit={3}
        transition={customSlide}
        autoClose={TOAST_AUTO_CLOSE_MS}
        pauseOnHover
        pauseOnFocusLoss
        icon={({ type }) => {
            if (type === "error") {
                return <AlertOutlinedIcon className="stroke-gray-700" />;
            }
            return null;
        }}
    />
);

// AppShell swaps between two ToastContainers that share react-toastify's
// default container id (logged-in tree inside OverlaysProvider, public tree
// without it). On its first render a new container reads the toast list of the
// container still registered under that id, which only unregisters in its
// effect cleanup, so private toasts rendered outside OverlaysProvider and threw
// "useOverlays must be used within a OverlayProvider" (Sentry
// KICK-FRONTEND-39G). Mounting one commit later lets the previous container
// unsubscribe first; toasts fired meanwhile are queued and shown on mount.
export const ToastContainer: React.FC = () => {
    const [
        hasPreviousContainerUnsubscribed,
        setHasPreviousContainerUnsubscribed,
    ] = useState(false);

    useEffect(() => {
        setHasPreviousContainerUnsubscribed(true);
    }, []);

    return hasPreviousContainerUnsubscribed ? (
        <ConfiguredToastContainer />
    ) : null;
};
